import { use, useLayoutEffect, useRef, useState } from "react";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";

import { OcrRendererContext } from "@bokio/components/OcrRenderer/OcrRenderer";
import { ButtonGroup, IconButton } from "@bokio/designsystem/components/Button";
import { Paragraph } from "@bokio/designsystem/components/TypographicElements/TypographicElements";
import { useDeviceQuery } from "@bokio/elements/DeviceQuery/useDeviceQuery";
import { GeneralLangFactory } from "@bokio/lang";
import { mergeClassNames } from "@bokio/utils/classes";

import type { FC } from "react";
import type { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";

import * as styles from "../../receipt.scss";

//import { useToggle } from "@bokio/hooks/useToggle/useToggle";

export interface ReceiptPageViewRef {
	imageRef: HTMLImageElement | null;
}

export type RenderActionNodeInButtonGroup = (props: { shouldDisableActions?: boolean }) => React.JSX.Element;

type ReceiptPageViewProps = {
	downloadUrl?: string;
	imageUrl: string;
	onRotate?: () => void;
	/**
	 * These extra loading states are for avoiding flickering UI.
	 */
	isRotating?: boolean;
	isDeleting?: boolean;
	download?: string;
	onImageRef?: (imgRef: HTMLImageElement | null) => void;
	isPdf: boolean;
	overlay?: boolean;
	className?: string;
	imgTestId?: string;
} & (
	| {
			/**
			 * Kept for legacy compatibility, prefer `allowDeletePageAndCallbackAndExecuteDeleteWith` and `allowDeleteEntireReceiptAndPerformDeleteUsing` when possible.
			 */
			onDelete?: () => void;
	  }
	| {
			renderActionNodeInButtonGroup: RenderActionNodeInButtonGroup;
	  }
);

type ReceiptPageViewInnerProps = ReceiptPageViewProps & { isPanning: boolean };

const ReceiptPageViewInner: FC<ReceiptPageViewInnerProps> = ({
	download,
	downloadUrl,
	onRotate,
	imageUrl,
	isDeleting,
	isRotating,
	onImageRef,
	isPdf,
	overlay,
	imgTestId,
	isPanning,
	...props
}) => {
	const { zoomIn, zoomOut } = useControls();
	const { isDesktop } = useDeviceQuery();
	const [isTransitioning, setIsTransitioning] = useState(true);
	const generalLang = GeneralLangFactory();

	const imageRef = useRef<HTMLImageElement>(null);
	const shouldDisableActions = isDeleting || isRotating || isTransitioning;

	// This runs before DOM changes are actually reflected => thus always later than image starts loading.
	// Not using `onLoadStart` event handler because it's not fired at all in Chrome.
	useLayoutEffect(() => {
		setIsTransitioning(true);
	}, [imageUrl]);

	const rotate = () => {
		if (onRotate) {
			setIsTransitioning(true);
			onRotate();
		}
	};

	const onImageLoad = () => {
		setIsTransitioning(false);
		onImageRef && onImageRef(imageRef.current);
	};

	return (
		<>
			<div className={styles.imgAndButtonContainer}>
				<div className={styles.buttonContainer}>
					<ButtonGroup direction="row" align="start">
						{isDesktop && (
							<>
								<IconButton
									icon="zoom-out"
									size="small"
									disabled={shouldDisableActions}
									onClick={() => zoomOut(0.5)}
									title={generalLang.ReceiptZoomInShortcut}
								/>
								<IconButton
									icon="zoom-in"
									size="small"
									disabled={shouldDisableActions}
									onClick={() => zoomIn(0.5)}
									title={generalLang.ReceiptZoomInShortcut}
								/>
							</>
						)}
						{downloadUrl && (
							<IconButton
								type="link"
								size="small"
								disabled={shouldDisableActions}
								external
								route={downloadUrl}
								icon="download-alt"
								target="_blank"
								download={download}
							/>
						)}
						{onRotate && !isPdf && (
							<IconButton
								icon="cw"
								size="small"
								disabled={shouldDisableActions}
								onClick={rotate}
								testId="RotateReceiptPageButton"
							/>
						)}
						{"onDelete" in props && props.onDelete && (
							<IconButton
								icon="trash-empty"
								size="small"
								disabled={shouldDisableActions}
								onClick={props.onDelete}
								testId="DeleteReceiptPageButton"
							/>
						)}
						{"renderActionNodeInButtonGroup" in props && props.renderActionNodeInButtonGroup({ shouldDisableActions })}
					</ButtonGroup>
				</div>
				<div
					className={mergeClassNames(
						styles.imageContainer,
						overlay && styles.overlayImage,
						isPanning && styles.imageContainerPanning,
					)}
				>
					<TransformComponent>
						<img
							data-testid={imgTestId}
							onLoad={onImageLoad}
							ref={imageRef}
							className={mergeClassNames(styles.img)}
							alt={generalLang.Receipt}
							src={imageUrl}
						/>
					</TransformComponent>
				</div>
			</div>

			{(isDeleting || isRotating || isTransitioning) && (
				<div className={styles.processingStateOverlayBase}>
					<div className={styles.processingStateTextWrapper}>
						<Paragraph>
							<span className={styles.processingStateText}>
								{isDeleting ? generalLang.Deleting : isRotating ? generalLang.Rotating : generalLang.Loading}
								{"..."}
							</span>
						</Paragraph>
					</div>
				</div>
			)}
		</>
	);
};

export const ReceiptPageView: FC<ReceiptPageViewProps> = ({ className, ...props }) => {
	const [isPanning, setIsPanning] = useState(false);

	const ocr = use(OcrRendererContext);
	const handleZoomState = (ref: ReactZoomPanPinchRef) => {
		ocr.setZoomState({ scale: ref.state.scale, positionX: ref.state.positionX, positionY: ref.state.positionY });
		ocr.rerenderOcr();
	};

	return (
		<div className={className} tabIndex={-1}>
			<TransformWrapper
				onTransformed={handleZoomState}
				onPanningStart={() => setIsPanning(true)}
				onPanning={handleZoomState}
				onPanningStop={() => {
					setIsPanning(false);
				}}
				alignmentAnimation={{ sizeX: 0, sizeY: 0, disabled: true }}
				velocityAnimation={{ disabled: true }}
				panning={{ velocityDisabled: true }}
				wheel={{ activationKeys: ["Control"] }}
				minScale={1}
				maxScale={5}
				disablePadding
			>
				<ReceiptPageViewInner {...props} isPanning={isPanning} />
			</TransformWrapper>
		</div>
	);
};
